<template>
    <div>
        <DotsLoading v-if="isLoading" />
        <template v-else>
            <div class="stop-header-buttons">
                <SearchRouteButton :date="computedDate" />
                <PrintButton class="stop-print-button" />
            </div>
            <div class="stop-container">
                <DatepickerBar v-model="selectedDate" :invalid-date="query.fd" />
                <div class="stop-content">
                    <h2 class="stop-title">
                        <strong v-if="stop && stop.noArret && isCurrentDay">{{ t('next_departures_stop') }}</strong>
                        <strong v-else-if="!isCurrentDay">{{ t('routes_at_stop') }}</strong>
                        <strong v-else>{{ t('stop_unavailable') }}</strong>
                        <span
                            v-if="stop && stop.noArret"
                            class="stop-name"
                        >
                            {{ stop.noArret }} - {{ stop.nom }}
                            <small>
                                {{ stop.description }}
                                <div class="accessible-icon">
                                    <img v-if="stop.accessible" src="@@/img/accessible.svg" width="18" height="18" />
                                    <span v-if="stop.accessible" class="visuallyhidden">
                                        {{ $t('a11y.accessible.accessible_stop') }}
                                    </span>
                                </div>
                            </small>
                        </span>
                    </h2>
                    <ul class="station-list no-bullet">
                        <li v-for="(station, i) in virtualStations" :key="`station-${i}`">
                            <VirtualStation
                                :station="station"
                                :route="station.parcours"
                                :has-notices="hasRouteNotices(station.parcours.noParcours, station.parcours.codeDirection)"
                                :is-today="isCurrentDay"
                                :ntr-enabled="ntrEnabled"
                                @refresh="startNextDepartureLoop"
                            >
                                <template #additional-content>
                                    <div class="details-link-wrapper">
                                        <RouterLink
                                            v-if="station.typeParcours === 'PP'"
                                            :to="{
                                                name: 'route-stop',
                                                params: {
                                                    route: station.parcours.noParcours,
                                                    stop: `${stop.noArret}-${slugify(stop.nom)}`,
                                                    direction: directionSlug(station.parcours.codeDirection, station.parcours.descriptionDirection),
                                                    date: !isCurrentDay ? selectedDate : undefined,
                                                },
                                            }"
                                            class="details-link button is-outlined"
                                        >
                                            {{ t('detailed_schedule') }}
                                        </RouterLink>
                                        <p v-if="station.typeParcours === 'PX'">{{ t('x_route') }}</p>
                                        <p v-if="station.typeParcours === 'DT'">{{ t('diverted_route') }}</p>
                                        <p v-if="station.typeParcours === 'NP'">{{ t('unplanned_route') }}</p>
                                        <p v-if="station.typeParcours === 'NV'">{{ t('videotron_shuttle') }}</p>
                                    </div>
                                </template>
                            </VirtualStation>
                        </li>
                    </ul>
                    <div class="stop-footer">
                        <ScheduleLegend class="stop-legend">
                            <li class="legend-item">
                                <span class="icon">
                                    <IconRealTime width="20" height="20" />
                                </span>
                                {{ $t('legend.real_time') }}
                            </li>
                            <li class="legend-item">
                                <span class="icon pulse">
                                    <IconBus width="24" height="24" />
                                </span>
                                {{ $t('legend.upcoming_departure') }}
                            </li>
                        </ScheduleLegend>
                        <TabList class="stop-tabs" heading-level="h3" :tabs="stopTabs">
                            <template #tab0>
                                <RouteMap :stop="stop" :zoom-level="13" />
                            </template>
                            <template #tab1>
                                <TripPlanner
                                    :default-position="`${stop.latitude}, ${stop.longitude}`"
                                    :default-address="`${stop.noArret} ${stop.nom}`"
                                />
                            </template>
                        </TabList>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useHead } from '@unhead/vue';
import { ref, onMounted, computed, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

import { setHours, setMinutes } from '@@/js/utils/dateFns';
import { addDays, isBefore, isAfter, parseISO } from '@@/js/utils/dateFns';
import { constants } from '@@/js/utils/constants';
import { useSchedulesStore } from '@@/js/stores/schedules';
import { useEffectiveDate } from '@/schedules/composables/useEffectiveDate';
import { useBreadcrumb } from '@/schedules/composables/useBreadcrumb';
import slugify from '@@/js/utils/slugify';
import directionSlug from '@@/js/utils/directionSlug';
import DatepickerBar from '@/schedules/components/DatepickerBar';
import DotsLoading from '@/shared/DotsLoading';
import PrintButton from '@/schedules/components/PrintButton';
import RouteMap from '@/schedules/components/RouteMap';
import ScheduleLegend from '@/schedules/components/ScheduleLegend';
import SearchRouteButton from '@/schedules/components/SearchRouteButton';
import TabList from '@/shared/TabList';
import TripPlanner from '@/shared/TripPlanner';
import VirtualStation from '@/schedules/components/VirtualStation';

import IconRealTime from '@@/icons/realtime.svg';
import IconBus from '@@/icons/menu/bus.svg';

const jsonld = ref(null);

const script = computed(() =>
    jsonld.value
        ? [
              {
                  type: 'application/ld+json',
                  children: JSON.stringify(jsonld.value),
              },
          ]
        : [],
);

const { t } = useI18n({
    messages: {
        fr: {
            all_routes_title: 'Tous les parcours',
            stop_title: 'Arrêt {stop}',
            next_departures_stop: 'Prochains passages à l’arrêt',
            detailed_schedule: 'Horaire détaillé',
            stop_unavailable: 'Arrêt indisponible',
            x_route: 'Parcours X',
            diverted_route: 'Parcours en détour',
            unplanned_route: 'Parcours non planifié',
            videotron_shuttle: 'Navette Vidéotron',
            routes_at_stop: 'Parcours à cet arrêt',
            locate_stop: 'Localiser l’arrêt',
            make_itinerary: 'Faire un itinéraire',
        },
        en: {
            all_routes_title: 'All routes',
            stop_title: 'Stop {stop}',
            next_departures_stop: 'Next departures at this stop',
            detailed_schedule: 'Detailed schedule',
            stop_unavailable: 'Stop unavailable',
            x_route: 'X route',
            diverted_route: 'Diverted route',
            unplanned_route: 'Unplanned route',
            videotron_shuttle: 'Vidéotron shuttle',
            routes_at_stop: 'Routes at this stop',
            locate_stop: 'Locate stop',
            make_itinerary: 'Make an itinerary',
        },
    },
});

const isLoading = ref(true);
const stop = ref();

useHead({
    script,
    title: () => isLoading.value ? `${t('all_routes_title')} | RTC` : `${t('stop_title', { stop: stop.value.noArret })} | RTC`,
});

const vRoute = useRoute();
const { params, name, query } = vRoute;
const router = useRouter();

const { getEffectiveDate, isToday } = useEffectiveDate();
const selectedDate = ref(params.date || getEffectiveDate(new Date(), 'yyyy-MM-dd'));
const computedDate = computed(() => {
    return !isToday(selectedDate.value) ? selectedDate.value : undefined;
});
const isCurrentDay = computed(() => isToday(selectedDate.value));

const stopTabs = [
    { name: t('locate_stop') },
    { name: t('make_itinerary') },
];

const schedulesStore = useSchedulesStore();
const { fetchStopDepartures, fetchStop, hasRouteNotices, sortStations } = schedulesStore;

watch(selectedDate, (newDate) => {
    router.replace({ name, params: { ...params, date: newDate } });
});

const virtualStations = ref([]);

const nextDeparturesLoop = ref(null);
const timeout = ref(null);
const pageRefreshLoop = ref(null);
const initialDate = ref(null);
const ntrEnabled = computed(() => nextDeparturesLoop.value !== null ? !!nextDeparturesLoop.value : nextDeparturesLoop.value);

function stopLoop() {
    stopNextDepartureLoop();
    timeout.value && clearTimeout(timeout.value);
}

function stopPageRefreshLoop() {
    pageRefreshLoop.value && clearInterval(pageRefreshLoop.value);
}

function stopNextDepartureLoop() {
    nextDeparturesLoop.value && clearInterval(nextDeparturesLoop.value);
    nextDeparturesLoop.value = false;
}

function startPageRefreshLoop() {
    stopPageRefreshLoop();
    initialDate.value = new Date();
    pageRefreshLoop.value = setInterval(validateDay, 60 * 1000);
}

function validateDay() {
    const nextDay = addDays(parseISO(selectedDate.value), 1);
    const refreshDate = setMinutes(setHours(nextDay, 4), 15);
    if (isAfter(new Date(), refreshDate)) {
        window.location.reload();
    }
}

function startNextDepartureLoop() {
    if (nextDeparturesLoop.value != null) {
        updateNextDepartures();
    }
    const clearDelay = 2 * 60 * 60 * 1000; // 2 heures
    const refreshRate = 30 * 1000; // 30 secondes
    stopNextDepartureLoop();
    timeout.value = setTimeout(stopLoop, clearDelay);
    nextDeparturesLoop.value = setInterval(updateNextDepartures, refreshRate);
}

async function updateNextDepartures() {
    if (stop.value && stop.value.noArret) {
        const stations = await fetchStopDepartures(stop.value.noArret, selectedDate.value);
        if (stations) {
            virtualStations.value = stations;
            sortStations(virtualStations.value);
        }
    }
}

const { addBreadcrumbLinks } = useBreadcrumb();

onMounted(async () => {
    const range = constants.CALENDAR_RANGE;
    const today = getEffectiveDate(new Date(), 'yyyy-MM-dd');
    const lastAvailableDate = getEffectiveDate(addDays(new Date(), range), 'yyyy-MM-dd');
    const date = params.date || today;

    if (isBefore(date, today)) {
        router.replace({ name, params: { ...params, date: today } });
    }
    if (isAfter(date, lastAvailableDate)) {
        router.replace({ name, params: { ...params, date: today }, query: { fd: date } });
    }

    const noStop = params.stop.split('-')[0];
    virtualStations.value = await fetchStopDepartures(noStop, date);
    sortStations(virtualStations.value);
    stop.value = await fetchStop(noStop);
    if (!stop.value) {
        router.replace({ name: 'route-list' });
    }

    jsonld.value = {
        '@context': 'http://schema.org/',
        '@type': 'BusStop',
        name: `${stop.value.noArret} - ${stop.value.nom}`,
    };

    const pageTitle = document.querySelector('h1');
    pageTitle.innerText = `${t('stop_title', { stop: stop.value.noArret })}`;

    // Liens à ajouter au fil d'Ariane
    const links = [
        {
            'text': `${t('stop_title', { stop: stop.value.noArret })}`,
            'url': 'current-page',
        },
    ];
    addBreadcrumbLinks(links);

    isLoading.value = false;

    startPageRefreshLoop();
    if (isCurrentDay.value) {
        startNextDepartureLoop();
    }
});

onUnmounted(() => {
    stopLoop();
    stopPageRefreshLoop();
});
</script>

<style lang="scss" scoped>
.stop-header-buttons {
    margin-bottom: 16px;
    gap: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.stop-print-button {
    :deep(.print-button) {
        @include rem-typo(14px);
        line-height: 1;
        display: flex;
        align-items: center;
        border: 1px solid $color-blue-60;
        padding: 12px;
        border-radius: calc(infinity * 1px);
        gap: 8px;
        transition: background-color $default-transition, color $default-transition;
        @include breakpoint(medium) {
            @include rem-typo(16px);
            gap: 16px;
            padding: 12px 16px;
        }
        &:hover,
        &:focus {
            background-color: $color-blue-60;
            color: $color-white;
        }
    }
}
.stop-container {
    background-color: $color-white;
    border-radius: 20px;
    padding: 16px 16px 0;
    @include breakpoint(medium) {
        padding: 16px;
    }
}
.stop-content {
    @include breakpoint(medium) {
        padding: 0 16px 16px;
    }
}
.stop-title {
    @include rem-typo(20px);
    margin: 24px 0 16px;
}
.stop-name {
    @include rem-typo(18px);
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 8px;
    font-weight: 400;
    margin-top: 4px;
    small {
        @include rem-typo(12px);
        display: flex;
        align-items: center;
        gap: 8px;
        color: $color-black-100;
    }
}
.station-list {
    margin-bottom: 24px;
}
.details-link-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 16px;
    @include breakpoint(medium) {
        border-left: 1px solid $color-white;
        padding-left: 16px;
        margin-top: 0;
    }
}
.details-link {
    color: $color-white;
    border-color: $color-white;
}
.stop-legend {
    margin: 0;
}
.stop-footer {
    display: grid;
    gap: 16px;
    align-items: flex-start;
    @include breakpoint(medium) {
        grid-template-columns: repeat(2, 1fr);
    }
}
.stop-tabs {
    background-color: $color-black-2;
    border-radius: 15px;
    margin: 0 -16px;
    @include breakpoint(medium) {
        margin: 0;
    }
    :deep(.tab-list) {
        grid-template-columns: repeat(2, 1fr);
    }
    :deep(.tab-button) {
        @include breakpoint(medium) {
            @include rem-typo(16px);
        }
    }
}
.map-container {
    height: 300px;
    border-radius: 0 0 15px 15px;
}
</style>
